import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, ArrowRight, Palette, Code, Video, Camera, PenTool, Edit3, Megaphone, Sparkles,
  Briefcase, Users, Handshake, Star, CheckCircle, Clock, Euro, MapPin, Heart,
  Rocket, TrendingUp, Gift, Award, UserPlus, Building2, User, Zap, Target
} from 'lucide-react';
import { cn } from '../../lib/utils';

const JoinUsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('creatives');

  // Types de profils créatifs recherchés
  const creativeProfiles = [
    {
      id: 'graphic-design',
      title: 'Graphiste / Directeur Artistique',
      icon: Palette,
      color: 'from-pink-500 to-rose-600',
      description: 'Créez des identités visuelles percutantes et des supports de communication innovants.',
      skills: ['Adobe Creative Suite', 'Branding', 'Print & Digital', 'Direction artistique'],
      type: 'Freelance / Ponctuel'
    },
    {
      id: 'web-development',
      title: 'Développeur Web',
      icon: Code,
      color: 'from-cyan-500 to-blue-600',
      description: 'Développez des sites web performants et des applications sur mesure.',
      skills: ['React / Vue.js', 'PHP / Node.js', 'WordPress', 'E-commerce'],
      type: 'Freelance / Ponctuel'
    },
    {
      id: 'video-production',
      title: 'Vidéaste / Monteur',
      icon: Video,
      color: 'from-purple-500 to-violet-600',
      description: 'Captez et montez des vidéos qui racontent des histoires captivantes.',
      skills: ['Tournage', 'Montage', 'Motion Design', 'Drone'],
      type: 'Freelance / Ponctuel'
    },
    {
      id: 'motion-design',
      title: 'Motion Designer',
      icon: Sparkles,
      color: 'from-amber-500 to-orange-600',
      description: 'Donnez vie aux marques avec des animations créatives et impactantes.',
      skills: ['After Effects', 'Animation 2D/3D', 'Lottie', 'VFX'],
      type: 'Freelance / Ponctuel'
    },
    {
      id: 'photography',
      title: 'Photographe',
      icon: Camera,
      color: 'from-emerald-500 to-green-600',
      description: 'Immortalisez les moments clés avec un œil créatif et professionnel.',
      skills: ['Portrait', 'Produit', 'Événementiel', 'Retouche'],
      type: 'Freelance / Ponctuel'
    },
    {
      id: 'copywriting',
      title: 'Rédacteur / Copywriter',
      icon: Edit3,
      color: 'from-blue-500 to-indigo-600',
      description: 'Rédigez des contenus qui captent l\'attention et convertissent.',
      skills: ['SEO', 'Storytelling', 'Réseaux sociaux', 'Publicité'],
      type: 'Freelance / Ponctuel'
    },
    {
      id: 'social-media',
      title: 'Community Manager',
      icon: Megaphone,
      color: 'from-red-500 to-pink-600',
      description: 'Animez les communautés et développez la présence digitale des marques.',
      skills: ['Stratégie sociale', 'Création de contenu', 'Analytics', 'Ads'],
      type: 'Freelance / Ponctuel'
    },
    {
      id: 'illustration',
      title: 'Illustrateur',
      icon: PenTool,
      color: 'from-teal-500 to-cyan-600',
      description: 'Créez des illustrations uniques qui donnent une identité forte aux projets.',
      skills: ['Illustration digitale', 'Character design', 'Infographie', 'Style personnalisé'],
      type: 'Freelance / Ponctuel'
    }
  ];

  // Avantages de rejoindre l'agence
  const advantages = [
    { icon: Briefcase, title: 'Projets variés', description: 'Travaillez sur des projets diversifiés pour des clients de tous secteurs.' },
    { icon: Euro, title: 'Tarifs compétitifs', description: 'Nous proposons des tarifs justes et un paiement rapide.' },
    { icon: Users, title: 'Réseau de talents', description: 'Intégrez un collectif de créatifs passionnés et collaborez ensemble.' },
    { icon: TrendingUp, title: 'Progression', description: 'Évoluez vers le statut de créatif intégré avec des missions régulières.' },
    { icon: Heart, title: 'Accompagnement', description: 'Bénéficiez d\'un suivi personnalisé et de conseils pour développer votre activité.' },
    { icon: Award, title: 'Reconnaissance', description: 'Vos réalisations sont valorisées et mises en avant dans notre portfolio.' }
  ];

  // Processus de candidature
  const processSteps = [
    { step: 1, title: 'Créez votre profil', description: 'Remplissez le formulaire avec vos informations et portfolio.' },
    { step: 2, title: 'Validation', description: 'Notre équipe examine votre candidature sous 48h.' },
    { step: 3, title: 'Signature du contrat', description: 'Signez le contrat de partenariat en ligne.' },
    { step: 4, title: 'Première mission', description: 'Recevez votre première proposition de mission !' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/site')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </button>
          <Link to="/login" className="text-sm text-cyan-600 hover:text-cyan-700 font-medium">
            Déjà membre ? Connexion
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-orange-500/5" />
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-200">
              <Handshake className="w-3 h-3 mr-1" />
              Rejoignez l'aventure MSC
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Nous rejoindre
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-8">
              Vous êtes créatif freelance ou souhaitez devenir apporteur d'affaires ? 
              Rejoignez notre réseau de talents et développez votre activité avec nous.
            </p>
            
            {/* Tabs */}
            <div className="inline-flex bg-slate-100 rounded-full p-1 mb-8">
              <button
                onClick={() => setActiveTab('creatives')}
                className={cn(
                  "px-6 py-2.5 rounded-full font-medium transition-all",
                  activeTab === 'creatives' 
                    ? "bg-white text-slate-900 shadow-sm" 
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                <Palette className="w-4 h-4 inline mr-2" />
                Créatifs
              </button>
              <button
                onClick={() => setActiveTab('business')}
                className={cn(
                  "px-6 py-2.5 rounded-full font-medium transition-all",
                  activeTab === 'business' 
                    ? "bg-white text-slate-900 shadow-sm" 
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                <Handshake className="w-4 h-4 inline mr-2" />
                Apporteurs d'affaires
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Content based on active tab */}
      {activeTab === 'creatives' ? (
        <>
          {/* Creative Profiles Section */}
          <section className="py-12 bg-white">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                  Profils recherchés
                </h2>
                <p className="text-slate-600 max-w-2xl mx-auto">
                  Nous recherchons des talents créatifs passionnés pour des missions ponctuelles ou régulières.
                  Découvrez les profils que nous recherchons actuellement.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {creativeProfiles.map((profile) => {
                  const Icon = profile.icon;
                  return (
                    <Card 
                      key={profile.id} 
                      className="group hover:shadow-lg transition-all cursor-pointer border-slate-200 hover:border-slate-300"
                      onClick={() => navigate('/devenir-partenaire')}
                    >
                      <CardContent className="p-5">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br text-white",
                          profile.color
                        )}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <h3 className="font-bold text-slate-900 mb-2 group-hover:text-cyan-600 transition-colors">
                          {profile.title}
                        </h3>
                        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                          {profile.description}
                        </p>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {profile.skills.slice(0, 2).map((skill, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 text-slate-500">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                        <Badge className="bg-slate-100 text-slate-600 text-[10px]">
                          {profile.type}
                        </Badge>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Advantages Section */}
          <section className="py-12 bg-slate-50">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                  Pourquoi nous rejoindre ?
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {advantages.map((adv, i) => {
                  const Icon = adv.icon;
                  return (
                    <div key={i} className="flex items-start gap-4 p-5 bg-white rounded-xl border border-slate-200">
                      <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{adv.title}</h3>
                        <p className="text-sm text-slate-600">{adv.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Process Section */}
          <section className="py-12 bg-white">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                  Comment ça marche ?
                </h2>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
                {processSteps.map((step, i) => (
                  <React.Fragment key={step.step}>
                    <div className="text-center max-w-[200px]">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-3">
                        {step.step}
                      </div>
                      <h4 className="font-semibold text-slate-900 mb-1">{step.title}</h4>
                      <p className="text-sm text-slate-600">{step.description}</p>
                    </div>
                    {i < processSteps.length - 1 && (
                      <ArrowRight className="w-6 h-6 text-slate-300 hidden md:block flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section for Creatives */}
          <section className="py-16 bg-gradient-to-br from-cyan-500 to-blue-600">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Prêt à nous rejoindre ?
              </h2>
              <p className="text-cyan-100 mb-8 max-w-2xl mx-auto">
                Créez votre profil créatif en quelques minutes et commencez à recevoir des propositions de missions.
              </p>
              <Button 
                onClick={() => navigate('/devenir-partenaire')}
                size="lg"
                className="bg-white text-cyan-600 hover:bg-cyan-50 rounded-full px-8 font-bold"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Créer mon profil créatif
              </Button>
            </div>
          </section>
        </>
      ) : (
        <>
          {/* Business Introducer Section */}
          <section className="py-12 bg-white">
            <div className="max-w-6xl mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <Badge className="mb-4 bg-orange-100 text-orange-700 border-orange-200">
                    Programme partenaire
                  </Badge>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                    Devenez apporteur d'affaires
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Vous connaissez des entreprises qui ont besoin de services de communication ? 
                    Devenez apporteur d'affaires et gagnez une commission sur chaque projet que vous nous apportez.
                  </p>
                  
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">Commission attractive</p>
                        <p className="text-sm text-slate-600">Recevez une commission sur chaque projet concrétisé</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">Suivi transparent</p>
                        <p className="text-sm text-slate-600">Suivez vos recommandations et vos gains en temps réel</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">Sans engagement</p>
                        <p className="text-sm text-slate-600">Pas d'objectifs, pas de contraintes - recommandez quand vous voulez</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border border-orange-200">
                  <h3 className="font-bold text-slate-900 mb-6 text-lg">Qui peut devenir apporteur ?</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-orange-100">
                      <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Clients satisfaits</p>
                        <p className="text-sm text-slate-600">Vous êtes client MSC et souhaitez nous recommander</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-orange-100">
                      <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <Handshake className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Partenaires professionnels</p>
                        <p className="text-sm text-slate-600">Comptables, avocats, consultants, coachs...</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-orange-100">
                      <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Indépendants & Freelances</p>
                        <p className="text-sm text-slate-600">Vous avez un réseau professionnel actif</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How it works for Business */}
          <section className="py-12 bg-slate-50">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                  Comment ça fonctionne ?
                </h2>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white font-bold text-2xl flex items-center justify-center mx-auto mb-4">
                    1
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Inscrivez-vous</h4>
                  <p className="text-slate-600">
                    Créez votre compte apporteur d'affaires en quelques clics. C'est gratuit et sans engagement.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white font-bold text-2xl flex items-center justify-center mx-auto mb-4">
                    2
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Recommandez</h4>
                  <p className="text-slate-600">
                    Partagez votre lien de parrainage ou mettez-nous directement en relation avec vos contacts.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white font-bold text-2xl flex items-center justify-center mx-auto mb-4">
                    3
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Gagnez</h4>
                  <p className="text-slate-600">
                    Recevez votre commission dès que le projet est signé. Suivi en temps réel depuis votre espace.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Commission Info */}
          <section className="py-12 bg-white">
            <div className="max-w-4xl mx-auto px-4">
              <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
                      <Gift className="w-12 h-12 text-white" />
                    </div>
                    <div className="text-center md:text-left">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">
                        Commission de 10% sur chaque projet
                      </h3>
                      <p className="text-slate-600 mb-4">
                        Pour chaque projet signé grâce à votre recommandation, vous recevez 10% du montant HT. 
                        Paiement sous 30 jours après encaissement du projet.
                      </p>
                      <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                        <Badge className="bg-white border-orange-200 text-orange-700">
                          <Euro className="w-3 h-3 mr-1" />
                          Paiement rapide
                        </Badge>
                        <Badge className="bg-white border-orange-200 text-orange-700">
                          <Target className="w-3 h-3 mr-1" />
                          Sans plafond
                        </Badge>
                        <Badge className="bg-white border-orange-200 text-orange-700">
                          <Zap className="w-3 h-3 mr-1" />
                          Sans minimum
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* CTA Section for Business */}
          <section className="py-16 bg-gradient-to-br from-orange-500 to-amber-500">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Prêt à devenir apporteur d'affaires ?
              </h2>
              <p className="text-orange-100 mb-8 max-w-2xl mx-auto">
                Inscrivez-vous gratuitement et commencez à gagner des commissions sur vos recommandations.
              </p>
              <Button 
                onClick={() => navigate('/devenir-partenaire')}
                size="lg"
                className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 font-bold"
              >
                <Handshake className="w-5 h-5 mr-2" />
                Devenir apporteur d'affaires
              </Button>
            </div>
          </section>
        </>
      )}

      {/* FAQ Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
            Questions fréquentes
          </h2>
          
          <div className="space-y-4">
            {activeTab === 'creatives' ? (
              <>
                <details className="bg-white rounded-xl border border-slate-200 p-5 group">
                  <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    Quel statut faut-il pour rejoindre le réseau ?
                    <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="text-slate-600 mt-3">
                    Vous devez avoir un statut professionnel (auto-entrepreneur, société, portage salarial...) pour pouvoir facturer vos prestations.
                  </p>
                </details>
                <details className="bg-white rounded-xl border border-slate-200 p-5 group">
                  <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    Comment sont fixés les tarifs ?
                    <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="text-slate-600 mt-3">
                    Vous définissez votre taux horaire ou vos tarifs forfaitaires. Nous vous proposons des missions en accord avec vos tarifs et nous négocions ensemble le budget avec le client.
                  </p>
                </details>
                <details className="bg-white rounded-xl border border-slate-200 p-5 group">
                  <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    Puis-je refuser une mission ?
                    <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="text-slate-600 mt-3">
                    Absolument ! Vous êtes libre d'accepter ou de refuser les missions proposées selon vos disponibilités et vos préférences.
                  </p>
                </details>
              </>
            ) : (
              <>
                <details className="bg-white rounded-xl border border-slate-200 p-5 group">
                  <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    Y a-t-il un engagement ou des objectifs ?
                    <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="text-slate-600 mt-3">
                    Non, aucun engagement ni objectif. Vous recommandez quand vous le souhaitez, à votre rythme. Le programme est totalement flexible.
                  </p>
                </details>
                <details className="bg-white rounded-xl border border-slate-200 p-5 group">
                  <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    Comment est calculée la commission ?
                    <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="text-slate-600 mt-3">
                    La commission est de 10% du montant HT du premier projet signé avec le client que vous nous avez recommandé. Elle est versée après encaissement du projet.
                  </p>
                </details>
                <details className="bg-white rounded-xl border border-slate-200 p-5 group">
                  <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    Comment suivre mes recommandations ?
                    <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="text-slate-600 mt-3">
                    Depuis votre espace personnel, vous pouvez suivre en temps réel le statut de vos recommandations et le montant de vos commissions.
                  </p>
                </details>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} MSC L'Agence - Tous droits réservés
          </p>
          <div className="flex justify-center gap-4 mt-4 text-sm">
            <Link to="/site" className="text-slate-500 hover:text-white transition-colors">
              Retour au site
            </Link>
            <Link to="/site/mentions-legales" className="text-slate-500 hover:text-white transition-colors">
              Mentions légales
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default JoinUsPage;
